import { HighlightText } from "@/components/MedicalChronology/HighlightText";
import { CompactMarkdown } from "@/components/ui/Markdown";
import { cn } from "@paribus/shared-ui";
import React from "react";

export interface SummaryHighlightCardProps {
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  borderColor: string;
  value: string | null | undefined;
  isEditMode: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
  /** When true, body is plain text with {@link HighlightText}; when false, {@link CompactMarkdown}. */
  isSearching: boolean;
  highlightQuery: string;
}

export const SummaryHighlightCard: React.FC<SummaryHighlightCardProps> = ({
  title,
  icon,
  iconBg,
  borderColor,
  value,
  isEditMode,
  onChange,
  placeholder,
  isSearching,
  highlightQuery,
}) => {
  const displayValue = value?.trim() ? value : null;

  return (
    <div className="group relative overflow-hidden rounded-xl border border-neutral-100 bg-card transition-all hover:shadow-md">
      <div className="flex items-center gap-2.5 px-4 pb-2 pt-3">
        <div className={cn("flex items-center justify-center rounded-xl p-2", iconBg)}>{icon}</div>
        <h3 className="text-sm font-bold tracking-tight text-neutral-900">
          <HighlightText text={title} highlightQuery={highlightQuery} />
        </h3>
      </div>

      <div className="border-t border-neutral-50">
        {isEditMode ? (
          <div className="p-3">
            <textarea
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value)}
              rows={12}
              className="min-h-[16rem] w-full resize-y rounded-lg border border-neutral-200 bg-neutral-50/50 px-4 py-3 text-dense font-normal leading-relaxed text-neutral-900 shadow-sm transition-all focus:bg-card focus-visible:shadow-focus"
              placeholder={placeholder}
            />
          </div>
        ) : displayValue ? (
          <div
            className={cn(
              "border-l-[3px] px-3.5 py-3",
              borderColor,
              "mb-3 ml-3 mr-3 mt-0.5 rounded-lg bg-neutral-50/40"
            )}
          >
            {isSearching ? (
              <p className="whitespace-pre-wrap text-dense font-normal leading-[1.55] text-neutral-800">
                <HighlightText
                  text={displayValue.replace(/\\n/g, "\n")}
                  highlightQuery={highlightQuery}
                />
              </p>
            ) : (
              <CompactMarkdown
                content={displayValue}
                className="[&_ol]:font-normal [&_p]:font-normal [&_ul]:font-normal"
              />
            )}
          </div>
        ) : (
          <div className="px-4 py-3">
            <span className="text-dense font-normal italic text-neutral-400">
              {placeholder ?? "—"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
